/**
 * Lo que el listado, el ZIP y la búsqueda necesitan de UNA empresa (spec
 * 2026-10-07 §2 y §3), leído UNA vez al entrar:
 *  - la matriz de empresa del usuario, folders/{cid}/users/{uid} (root no la
 *    necesita);
 *  - las restricciones de carpeta, folders/{cid}/restricciones: quien puede
 *    restringir (rango ≥ 60, lib/rank) las lee todas porque las necesita el
 *    panel; los demás solo las suyas (array-contains).
 *
 * Efectivo en una carpeta = el de la EMPRESA (lib/permission_engine
 * resolveEffective, sin cadena de carpetas), salvo que la carpeta o un
 * ancestro esté restringida para el usuario: entonces la carpeta no existe
 * para él. Root nunca se restringe.
 *
 * Errores: loadCompanyAccess nunca rechaza. Matriz ilegible → 'error' y el
 * motor da el efectivo VACÍO (R2). Restricciones ilegibles → 'error' y
 * listingBlocked: la empresa muestra error y no lista nada (nunca se muestra
 * todo por un fallo de lectura). Módulo puro: el db llega por parámetro.
 */
import { companyMatrixPath, collectionPathOfEntry } from './folder_chain';
import { resolveEffective, emptyEffective } from './permission_engine';
import { RESTRICCIONES, normalizeRestrictions, isRestricted } from './folder_restrictions';

export const normalizeMatrix = (data) => {
  const p = data && typeof data === 'object' ? data.permissions : null;
  if (!p || typeof p !== 'object') return null;
  const out = {
    documents: p.documents && typeof p.documents === 'object' ? p.documents : {},
    folders: p.folders && typeof p.folders === 'object' ? p.folders : {},
  };
  // Abrir/cerrar que decide la empresa (lib/closed_folder_perms): solo booleanos.
  const c = p.carpetas && typeof p.carpetas === 'object' ? p.carpetas : null;
  if (c) {
    out.carpetas = {};
    ['abrir', 'cerrar'].forEach((k) => { if (typeof c[k] === 'boolean') out.carpetas[k] = c[k]; });
  }
  return out;
};

export const loadCompanyAccess = async ({ db, cid, uid, isRootUser = false, readsAll = false }) => {
  const access = {
    cid,
    uid,
    isRootUser: isRootUser === true,
    companyMatrix: null,
    companyMatrixState: 'missing',
    restrictions: [],
    restrictionsState: 'ok',
  };
  const matrixRead = access.isRootUser
    ? Promise.resolve()
    : Promise.resolve().then(() => db.doc(companyMatrixPath(cid, uid)).get()).then(
      (s) => {
        if (s && s.exists) {
          access.companyMatrix = normalizeMatrix(s.data());
          access.companyMatrixState = 'present';
        }
      },
      () => { access.companyMatrixState = 'error'; },
    );
  const restrRead = Promise.resolve().then(() => {
    const col = db.collection('folders').doc(cid).collection(RESTRICCIONES);
    return (readsAll ? col : col.where('uids', 'array-contains', uid)).get();
  }).then(
    (snap) => { access.restrictions = normalizeRestrictions(snap.docs.map((d) => d.data())); },
    () => { access.restrictionsState = 'error'; },
  );
  await Promise.all([matrixRead, restrRead]);
  return access;
};

/**
 * ¿Sigue abierta la misma empresa que cuando arrancó una carga? Si el usuario
 * cambió de empresa a mitad de una lectura, el resultado de la anterior se
 * descarta (no se mezcla su acceso con el estado de la nueva).
 */
export const sameCompany = (captured, current) => !!captured && !!current && !!captured.cid && captured.cid === current.cid;

export const listingBlocked = (access) => !access || (!access.isRootUser && access.restrictionsState === 'error');

export const folderHidden = ({ access, collectionPath }) => {
  if (!access) return true;
  if (access.isRootUser) return false;
  if (access.restrictionsState === 'error') return true;
  return isRestricted(collectionPath, access.uid, access.restrictions);
};

export const companyEffective = ({ access, role, isAdmin }) => {
  if (!access) return emptyEffective();
  if (access.isRootUser) return resolveEffective({ isRootUser: true });
  return resolveEffective({
    role,
    isAdmin: isAdmin === true,
    companyMatrix: access.companyMatrix,
    companyMatrixState: access.companyMatrixState,
  });
};

/** Sin collectionPath = raíz de la empresa (nunca se restringe). */
export const effectiveAt = ({ access, role, isAdmin, collectionPath }) => (
  collectionPath && folderHidden({ access, collectionPath })
    ? emptyEffective()
    : companyEffective({ access, role, isAdmin })
);

/** Entradas folders[] que el usuario puede ver. Root: todas (incluso rotas, como siempre). */
export const visibleChildren = ({ access, entries }) => {
  const list = Array.isArray(entries) ? entries : [];
  if (access && access.isRootUser) return list;
  return list.filter((e) => {
    const cp = e && e.reference ? collectionPathOfEntry(e.reference) : null;
    return !!cp && !folderHidden({ access, collectionPath: cp });
  });
};

export const showsFolders = (perms) => !!(perms && perms.folders && perms.folders.read);

export const showsFiles = (perms) => showsFolders(perms) && !!(perms.documents && perms.documents.read);

/** ZIP: «Leer» de la carpeta, «Leer» y «Descargar» archivos. */
export const zipAllows = (perms) => showsFiles(perms) && !!perms.documents.download;

export default loadCompanyAccess;
